import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { GetUserById$Params } from '@app/api/fn/users/get-user-by-id';
import { UserProfileDto } from '@app/api/models';
import { UsersApiService } from '@app/api/services';
import { HTTP_ERRORS_TRANSLOCO } from '@app/core/constants/app.constants';
import { SnackbarMessageService } from '@app/core/services/snackbar-message.service';
import { environment } from 'environments/environment';
import { catchError, map, Observable, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private _usersDataService = inject(UsersApiService);
  private _snackbarMessageService = inject(SnackbarMessageService);
  private http = inject(HttpClient);
  private baseUrl = environment.apiBaseUrl;

  private userDataSignal = signal<UserProfileDto | null>(null);
  private mainUserDataSignal = signal<UserProfileDto | null>(
    UserService.restoreFromSession('qualitime-main-ud')
  );

  private static restoreFromSession(key: string): UserProfileDto | null {
    try {
      const stored = sessionStorage.getItem(key);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  // TODO this method is used in different components,
  // TODO check if we need to return Observable<UserProfileDto> or use userDataSignal
  getUserDataById(id: GetUserById$Params): Observable<UserProfileDto | null> {
    return this._usersDataService.getUserById(id).pipe(
      map((user: UserProfileDto) => this.handleGetUserDataByIdSuccess(user, true)),
      catchError(() => this.handleGetUserDataByIdError())
    );
  }

  /**
   * Get user profile using acting-as endpoint.
   * The actingAs parameter is automatically added by the ActingAsInterceptor.
   * If the sub-doctor has no subspecialties or workplaces, they are cloned from the main account.
   */
  getUserProfileActingAs(): Observable<UserProfileDto | null> {
    return this.http.get<UserProfileDto>(`${this.baseUrl}/users/profile/acting-as`).pipe(
      map((user: UserProfileDto) => this.handleGetUserDataByIdSuccess(user)),
      catchError(() => this.handleGetUserDataByIdError())
    );
  }

  userData() {
    return this.userDataSignal();
  }

  mainUserData() {
    return this.mainUserDataSignal();
  }

  setUserData(user: UserProfileDto | null) {
    this.userDataSignal.set(user);
  }

  setMainUserData(user: UserProfileDto | null) {
    this.mainUserDataSignal.set(user);
  }

  private handleGetUserDataByIdSuccess(user: UserProfileDto, isMainUser: boolean = false): UserProfileDto {
    this.userDataSignal.set(user);
    // Guardamos los datos en la sesión para recuperarlos en caso de que los necesitemos y el usuario haya recargado la aplicación.
    const dataSavedOnSession = {
      id: user.id,
      email: user.email,
      name: user.name,
      surname: user.surname,
      picture: user.picture,
      createdAt: user.createdAt,
      subspecialties: user.subspecialties,
    };
    sessionStorage.setItem('qualitime-ud', JSON.stringify(dataSavedOnSession));
    // Also store main user data separately for header display
    if (isMainUser) {
      this.mainUserDataSignal.set(user);
      sessionStorage.setItem('qualitime-main-ud', JSON.stringify(dataSavedOnSession));
    }
    return user;
  }

  private handleGetUserDataByIdError(): Observable<null> {
    this.userDataSignal.set(null);
    this._snackbarMessageService.showError(
      HTTP_ERRORS_TRANSLOCO.userData_error
    );
    return of(null);
  }

  /**
   * Toggle whether the current user accepts appointments. When false, the user is
   * hidden from visitor search. Updates the cached userData signal so the UI stays in sync.
   */
  setMyAcceptsAppointments(accepts: boolean): Observable<void> {
    return this.http
      .patch<void>(`${this.baseUrl}/users/me/accepts-appointments`, { accepts })
      .pipe(
        tap(() => {
          const current = this.userDataSignal();
          if (current) {
            this.userDataSignal.set({ ...current, acceptsAppointments: accepts });
          }
        })
      );
  }
}
